import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import React, { FC, useMemo, useState } from 'react'
import { Field, Form } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { useMobile } from '../../Utils/hooks'
import { PrimaryButton } from '../Button'
import TextFieldWrapper from '../Form/TextField'
import CloseIcon from '@mui/icons-material/Close'
import { toast } from 'react-toastify'
import CheckInActions from '../../Redux/checkInRedux'
import { useDispatch, useSelector } from 'react-redux'
import Rating from '@mui/material/Rating'
import { RootState } from '../../Redux'

const maxWidth = 400

const useStyles = makeStyles()((theme: Theme) => ({
    button: {
        marginTop: theme.spacing(3),
    },
    content: {
        minWidth: 400,
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: theme.spacing(2),
    },
    dialog: {
        borderRadius: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            borderRadius: 0,
        },
    },
    form: {
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: maxWidth,
    },
    shade: {
        opacity: 0.5,
    },
    title: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: theme.palette.primary.main,
        '& span': {
            margin: 0,
            fontWeight: 700,
        },
    },
    row: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: theme.spacing(3),
        justifyContent: 'space-between',
        alignItems: 'space-between',
        width: '100%',
        maxWidth: maxWidth,
    },
    textField: {
        maxWidth: maxWidth,
        width: '100%',
    },
    checkboxField: {
        margin: theme.spacing(2, 0),
        display: 'flex',
        alignItems: 'center',
    },
    gradeLabel: {
        marginBottom: 8,
        fontWeight: 500,
    },
}))

interface FeedbackDialogProps {
    display: boolean
    setFeedbackDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const FeedbackDialog: FC<FeedbackDialogProps> = ({
    display,
    setFeedbackDialogOpen,
}) => {
    const { t } = useTranslation()
    const { classes } = useStyles()
    const dispatch = useDispatch()

    const isMobile = useMobile('sm')
    const [currentGrade, setCurrentGrade] = useState<number | undefined>()
    const [gradeOffice, setGradeOffice] = useState<number | undefined>()

    const query = new URLSearchParams(location.search)
    const token: string = query.get('token') ?? ''

    const checkIn = useSelector((state: RootState) => state.checkIn)

    const customerEmail = useMemo(
        () => checkIn.checkIn?.sale?.customer?.email,
        [checkIn.checkIn?.sale?.customer?.email]
    )

    const handleSubmit = (
        {
            grade,
            gradeOffice,
            feedbackText,
            wantsResponse,
            email,
        }: {
            grade: number | undefined,
            gradeOffice: number | undefined,
            feedbackText: string | undefined,
            wantsResponse: boolean | undefined
            email: string | undefined
        }
    ) => {
        const callbacks = []
        callbacks.push(() => setFeedbackDialogOpen(false))
        callbacks.push(() => toast.success(
            t('serviceCardContainer.feedbackSubmitOk')
        ))

        if (grade) dispatch(
            CheckInActions.giveFeedback(
                grade,
                gradeOffice,
                feedbackText,
                wantsResponse,
                email,
                token,
                callbacks
            )
        )
    }

    return (
        <Dialog
            slotProps={{
                paper: {
                    className: classes.dialog,
                },
            }}
            aria-labelledby="feedback-dialog-title"
            open={display}
            fullScreen={isMobile}
        >
            <DialogTitle
                id="feedback-dialog-title"
                classes={{ root: classes.title }}
            >
                <span>{t('serviceCardContainer.feedbackTitle')}</span>
                <IconButton onClick={() =>setFeedbackDialogOpen(false)}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.content}>
                <Form
                    onSubmit={({ feedbackText, wantsResponse, email }) =>
                        handleSubmit({
                            grade: currentGrade,
                            gradeOffice,
                            feedbackText,
                            wantsResponse,
                            email
                        })
                    }
                    render={({ handleSubmit, values }) => (
                        <form onSubmit={handleSubmit} className={classes.form}>
                            <div className={classes.row}>
                                <p className={classes.gradeLabel}>{t('serviceCardContainer.grade')}</p>
                                <Rating
                                    name="grade"
                                    value={currentGrade ?? null}
                                    max={5}
                                    onChange={(_, value) => setCurrentGrade(value ?? undefined)}
                                    size="large"
                                    getLabelText={(value) => `${t('serviceCardContainer.grade')} ${value}`}
                                />
                            </div>
                            <div className={classes.row}>
                                <p className={classes.gradeLabel}>{t('serviceCardContainer.gradeOffice')}</p>
                                <Rating
                                    name="gradeOffice"
                                    value={gradeOffice ?? null}
                                    max={5}
                                    onChange={(_, value) => setGradeOffice(value ?? undefined)}
                                    size="large"
                                    getLabelText={(value) => `${t('serviceCardContainer.gradeOffice')} ${value}`}
                                />
                            </div>
                            <Field name="feedbackText" type="text">
                                {(props) => (
                                    <TextFieldWrapper
                                        {...props}
                                        variant="outlined"
                                        multiline
                                        className={classes.textField}
                                        rows={3}
                                        placeholder={t(
                                            'serviceCardContainer.feedbackText',
                                        )}
                                    />
                                )}
                            </Field>
                            <Field name="wantsResponse" type="checkbox" initialValue={false}>
                                {({ input }) => (
                                    <label className={classes.checkboxField}>
                                        <input
                                            type="checkbox"
                                            name={input.name}
                                            onChange={input.onChange}
                                            onBlur={input.onBlur}
                                            onFocus={input.onFocus}
                                            checked={input.value}
                                        />
                                        {t('serviceCardContainer.wantsResponse')}
                                    </label>
                                )}
                            </Field>
                            {values.wantsResponse && !customerEmail && (
                                <Field
                                    name="email"
                                    validate={value => {
                                        if (!value) return t('errors.required');
                                        // Basic email regex
                                        if (!/^\S+@\S+\.\S+$/.test(value)) return t('errors.email');
                                        return undefined;
                                    }}
                                >
                                    {(props) => (
                                        <TextFieldWrapper
                                            {...props}
                                            variant="outlined"
                                            className={classes.textField}
                                            placeholder={t('common.email')}
                                            label={t('common.email')}
                                        />
                                    )}
                                </Field>
                            )}
                            <PrimaryButton
                                type="submit"
                                disabled={!currentGrade}
                                className={classes.button}
                            >
                                {t('common.save')}
                            </PrimaryButton>
                        </form>
                    )}
                />
            </DialogContent>
        </Dialog>
    )
}
